import { Injectable, inject } from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';

import {
    AddressUpdateService,
    Patient,
    PatientAddressUpdate,
    PatientService,
    ProblemInstanceService
} from 'planner-shared';
import {Address} from '../../shared/models/address.model';

@Injectable({
    providedIn: 'root'
})
export class PatientAddressService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly patientService = inject(PatientService);
    private readonly addressUpdateService = inject(AddressUpdateService);

    private readonly addressEditChanged$: Subject<Patient>;

    constructor() {
        this.addressEditChanged$ = new Subject();
    }

    public getPatientAddressEditStateChange(): Observable<Patient> {
        return this.addressEditChanged$.asObservable();
    }

    public patientAddressEditStateChanged(patient: Patient): void {
        this.addressEditChanged$.next(patient);
    }

    public initAddressForm(addressForm: FormGroup, patient: Patient): void {
        addressForm.reset();
        addressForm.controls.address.enable();
        addressForm.controls.addressDescription.enable();
        addressForm.controls.status.setValue(patient.addressValid);
        addressForm.controls.permanent.setValue(patient.addressIsPermanent);
        addressForm.controls.zipCodeWarning.setValue(patient.zipCodeWarning);
        addressForm.controls.zipCodeSuggestion.setValue(patient.zipCodeSuggestion);
        addressForm.controls.address.setValue(<Address>{
            streetNameAndHouseNumber: patient.streetAddressNameNumberAndLetter.trim(),
            zipCode: patient.zipCode,
            userSelectedOption: false
        });
        addressForm.controls.addressDescription.setValue(patient.addressDescription);

        addressForm.markAsPristine();
    }

    public restorePatientAddress(patient: Patient, addressForm: FormGroup): void {
        this.patientService.getPatientFromInstance(patient.patientId).subscribe(oldPatient => {
            patient.streetAddressNameNumberAndLetter = oldPatient.streetAddressNameNumberAndLetter;
            patient.addressDescription = oldPatient.addressDescription;
            patient.zipCode = oldPatient.zipCode;
            patient.addressIsPermanent = oldPatient.addressIsPermanent;

            this.initAddressForm(addressForm, patient);
        });
    }

    public updateAddressForPatient(patient: Patient, addressForm: FormGroup): Observable<void> {
        const patientAddressUpdate = <PatientAddressUpdate>{
            patientId: patient.patientId,
            streetAddressNameNumberAndLetter: addressForm.value.address.streetNameAndHouseNumber,
            addressDescription: addressForm.value.addressDescription,
            zipCode: addressForm.value.address.zipCode ? addressForm.value.address.zipCode : addressForm.value.zipCodeSuggestion,
            permanent: addressForm.value.permanent
        };

        setTimeout(() => this.mapPatientAddressUpdateToPatient(patient, patientAddressUpdate));

        this.disableAddressFormInputs(addressForm);

        return this.addressUpdateService.updateAddressForPatient(patientAddressUpdate).pipe(
            switchMap(result =>
                this.problemInstanceService.getProblemInstance().pipe(
                    tap(problemInstanceResult => {
                        problemInstanceResult.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles =
                            result.numberOfTasksWithoutMainResponsibles;
                        problemInstanceResult.problemInstance.taskWorkShiftCompatibility =
                            result.taskWorkShiftCompatibility;
                    })
                )
            ),
            switchMap(() => this.patientService.getPatientFromInstance(patient.patientId)),
            tap(patientFromInstance => {
                patientFromInstance.streetAddressNameNumberAndLetter =
                    patientAddressUpdate.streetAddressNameNumberAndLetter;
                patientFromInstance.addressDescription = patientAddressUpdate.addressDescription;
                patientFromInstance.zipCode = patientAddressUpdate.zipCode;
                patientFromInstance.addressIsPermanent = patientAddressUpdate.permanent;
            }),
            map(() => null)
        );
    }

    public disableAddressFormInputs(addressForm: FormGroup): void {
        addressForm.controls.address.disable();
        addressForm.controls.addressDescription.disable();
    }

    public onSaveError(patient: Patient, addressForm: FormGroup): void {
        patient.addressUpdating = false;
        addressForm.controls.address.enable();
        addressForm.controls.addressDescription.enable();
    }

    private mapPatientAddressUpdateToPatient(patient: Patient, patientAddressUpdate: PatientAddressUpdate): void {
        patient.streetAddressNameNumberAndLetter = patientAddressUpdate.streetAddressNameNumberAndLetter;
        patient.addressDescription = patientAddressUpdate.addressDescription;
        patient.zipCode = patientAddressUpdate.zipCode;
        patient.addressIsPermanent = patientAddressUpdate.permanent;
        patient.zipCodeSuggestion = null;
        patient.zipCodeWarning = false;
        patient.addressValid = true;
    }
}
